// Panel admin para editar las tarifas MercadoLibre:
//  1. Comisión % (app_config.ml_comision_pct)
//  2. Costo fijo por rango (app_config.ml_costo_fijo_rangos) — [{hasta, fijo}]
//  3. Tabla peso → costo Mercado Envíos UY estimado (app_config.ml_tarifa_envios_estimada) — [{hasta_kg, costo}]

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const KEYS = ['ml_comision_pct', 'ml_costo_fijo_rangos', 'ml_tarifa_envios_estimada']

function parseVal(v) {
  if (v == null) return null
  return typeof v === 'string' ? JSON.parse(v) : v
}

export default function TarifasML() {
  const [comision, setComision] = useState(13.5)
  const [rangosFijo, setRangosFijo] = useState([])    // [{hasta, fijo}]
  const [tarifaEnvios, setTarifaEnvios] = useState([]) // [{hasta_kg, costo}]
  const [original, setOriginal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase.from('app_config').select('key, value').in('key', KEYS)
    const map = {}
    for (const r of data || []) map[r.key] = parseVal(r.value)
    const c = Number(map.ml_comision_pct ?? 13.5)
    const r = Array.isArray(map.ml_costo_fijo_rangos) ? map.ml_costo_fijo_rangos : []
    const t = Array.isArray(map.ml_tarifa_envios_estimada) ? map.ml_tarifa_envios_estimada : []
    setComision(c); setRangosFijo(r); setTarifaEnvios(t)
    setOriginal({ comision: c, rangos: r, tarifa: t })
    setLoading(false)
  }

  function haCambios() {
    if (!original) return false
    return (
      original.comision !== comision ||
      JSON.stringify(original.rangos) !== JSON.stringify(rangosFijo) ||
      JSON.stringify(original.tarifa) !== JSON.stringify(tarifaEnvios)
    )
  }

  async function guardar() {
    setSaving(true); setMsg(null)
    const rangosOrd = [...rangosFijo].sort((a, b) => Number(a.hasta) - Number(b.hasta))
    const tarifaOrd = [...tarifaEnvios].sort((a, b) => Number(a.hasta_kg) - Number(b.hasta_kg))
    try {
      const ups = [
        { key: 'ml_comision_pct', value: comision },
        { key: 'ml_costo_fijo_rangos', value: rangosOrd },
        { key: 'ml_tarifa_envios_estimada', value: tarifaOrd },
      ]
      for (const r of ups) {
        const { error } = await supabase.from('app_config').upsert(r, { onConflict: 'key' })
        if (error) throw error
      }
      setRangosFijo(rangosOrd); setTarifaEnvios(tarifaOrd)
      setOriginal({ comision, rangos: rangosOrd, tarifa: tarifaOrd })
      setMsg({ ok: true, txt: '✓ Tarifas actualizadas. Se aplican a partir del próximo cálculo (ficha, simulador, sync).' })
    } catch (e) {
      setMsg({ ok: false, txt: 'Error: ' + e.message })
    }
    setSaving(false)
  }

  function descartar() {
    if (!original) return
    setComision(original.comision)
    setRangosFijo(original.rangos)
    setTarifaEnvios(original.tarifa)
    setMsg(null)
  }

  // ─── Helpers para CRUD de rangos costo fijo ───
  function addRangoFijo() {
    const ultimo = rangosFijo[rangosFijo.length - 1]
    setRangosFijo([...rangosFijo, { hasta: ultimo ? Number(ultimo.hasta) * 2 : 500, fijo: 20 }])
  }
  function delRangoFijo(idx) {
    if (!confirm('¿Quitar este rango?')) return
    setRangosFijo(rangosFijo.filter((_, i) => i !== idx))
  }
  function setRangoFijo(idx, campo, valor) {
    setRangosFijo(rangosFijo.map((r, i) => i === idx ? { ...r, [campo]: Number(valor) || 0 } : r))
  }

  // ─── Helpers para CRUD de tabla peso ───
  function addTarifaEnvio() {
    const ultimo = tarifaEnvios[tarifaEnvios.length - 1]
    setTarifaEnvios([...tarifaEnvios, { hasta_kg: ultimo ? Number(ultimo.hasta_kg) * 2 : 1, costo: 150 }])
  }
  function delTarifaEnvio(idx) {
    if (!confirm('¿Quitar este rango?')) return
    setTarifaEnvios(tarifaEnvios.filter((_, i) => i !== idx))
  }
  function setTarifaEnvioCampo(idx, campo, valor) {
    setTarifaEnvios(tarifaEnvios.map((r, i) => i === idx ? { ...r, [campo]: Number(valor) || 0 } : r))
  }

  // ─── Vista previa: ¿cuánto cobra a un producto de ejemplo? ───
  function calcularNeto(precio) {
    const rango = [...rangosFijo].sort((a, b) => a.hasta - b.hasta).find(r => precio < r.hasta)
    const com = Math.round(precio * comision / 100)
    const fijo = rango ? Number(rango.fijo) : 0
    return { com, fijo, neto: precio - com - fijo, rango }
  }

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/canales/mercadolibre" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a MercadoLibre</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <div>
            <h1 style={{ margin: 0 }}>💰 Tarifas MercadoLibre</h1>
            <p className="lead" style={{ margin: '4px 0 0', maxWidth: 720 }}>
              Comisión, costo fijo por rango y tabla peso → costo Mercado Envíos. Estos números alimentan la calculadora neta de cada ficha y el simulador de envío.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {haCambios() && (
              <button className="btn ghost" onClick={descartar} disabled={saving} style={{ fontSize: 12 }}>
                Descartar
              </button>
            )}
            <button
              onClick={guardar}
              disabled={saving || !haCambios()}
              style={{
                background: haCambios() ? '#1A3461' : '#CBD5E1', color: '#fff', border: 'none',
                padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                cursor: haCambios() && !saving ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Guardando…' : haCambios() ? 'Guardar todo' : 'Sin cambios'}
            </button>
          </div>
        </div>

        {msg && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 8,
            background: msg.ok ? '#DCFCE7' : '#FEE2E2',
            color: msg.ok ? '#166534' : '#7F1D1D',
            fontSize: 13, fontWeight: 600,
          }}>{msg.txt}</div>
        )}

        {loading ? (
          <div className="card" style={{ marginTop: 18, color: 'var(--muted)' }}>Cargando…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 18, marginTop: 18 }}>

            {/* COMISIÓN */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 18 }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 14, color: '#1A3461', fontWeight: 800 }}>1. Comisión MercadoLibre</h3>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748B' }}>
                % que ML retiene del precio publicado. Roberto confirmó 13,5% s/IVA (2026).
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  step="0.1"
                  value={comision}
                  onChange={e => setComision(Number(e.target.value) || 0)}
                  style={{ width: 100, padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 18, fontWeight: 800, color: '#1A3461' }}
                />
                <span style={{ fontSize: 16, color: '#475569' }}>%</span>
              </div>
            </div>

            {/* COSTO FIJO POR RANGO */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <h3 style={{ margin: '0 0 6px', fontSize: 14, color: '#1A3461', fontWeight: 800 }}>2. Costo fijo por rango de precio</h3>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
                    ML cobra $ fijos adicionales según precio. Si precio &lt; hasta → cobra fijo.
                  </p>
                </div>
                <button onClick={addRangoFijo} style={{ background: '#10B981', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Agregar</button>
              </div>
              <table style={{ width: '100%', marginTop: 8, fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E2E8F0', textAlign: 'left' }}>
                    <th style={{ padding: '6px 4px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Precio menor a</th>
                    <th style={{ padding: '6px 4px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Costo fijo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rangosFijo.map((r, i) => (
                    <tr key={i}>
                      <td style={{ padding: '4px 4px' }}>
                        <input type="number" value={r.hasta} onChange={e => setRangoFijo(i, 'hasta', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input type="number" value={r.fijo} onChange={e => setRangoFijo(i, 'fijo', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ width: 28, textAlign: 'right' }}>
                        <button onClick={() => delRangoFijo(i)} style={{ width: 22, height: 22, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#991B1B', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Preview con producto de ejemplo */}
              <div style={{ marginTop: 14, padding: 10, background: '#F8FAFC', borderRadius: 8, fontSize: 11, color: '#475569' }}>
                <strong style={{ color: '#1A3461' }}>Preview con ejemplos:</strong>
                {[100, 300, 600, 850, 1200].map(p => {
                  const c = calcularNeto(p)
                  return (
                    <div key={p} style={{ marginTop: 4 }}>
                      Precio <strong>${p}</strong> → comisión <strong>−${c.com}</strong> + fijo <strong>−${c.fijo}</strong> = neto <strong style={{ color: '#10B981' }}>${c.neto}</strong>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* TABLA PESO ML */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 18, gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <h3 style={{ margin: '0 0 6px', fontSize: 14, color: '#1A3461', fontWeight: 800 }}>3. Tarifa Mercado Envíos por peso facturable</h3>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
                    Lo usa el simulador del Tab Envío para estimar costo ML. Si <code>peso_facturable</code> ≤ <code>hasta_kg</code> → cobra ese costo. Cuando conectemos la API real de ML, esto se reemplaza por la consulta dinámica.
                  </p>
                </div>
                <button onClick={addTarifaEnvio} style={{ background: '#10B981', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Agregar</button>
              </div>
              <table style={{ width: '100%', marginTop: 8, fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E2E8F0', textAlign: 'left' }}>
                    <th style={{ padding: '6px 4px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Peso hasta (kg)</th>
                    <th style={{ padding: '6px 4px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Costo ML ($)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tarifaEnvios.map((r, i) => (
                    <tr key={i}>
                      <td style={{ padding: '4px 4px' }}>
                        <input type="number" step="0.1" value={r.hasta_kg} onChange={e => setTarifaEnvioCampo(i, 'hasta_kg', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <input type="number" value={r.costo} onChange={e => setTarifaEnvioCampo(i, 'costo', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ width: 28, textAlign: 'right' }}>
                        <button onClick={() => delTarifaEnvio(i)} style={{ width: 22, height: 22, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#991B1B', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
